<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      left-side-class="col-xl"
      right-side-class="
        col-xl align-items-end d-flex pb-2 justify-content-end
      "
    >
      <template v-slot:customActions>
        <action-button
          i="fas fa-arrow-circle-left"
          variant="outline-success m-2"
          @click="goBack()"
        >
          Regresar
        </action-button>

        <drop-down
          v-if="visiblePageActions.length > 1"
          :items="pageActions"
          class="outline-success m-2"
          normal-size
          always-show-dropdown
        />

        <action-button
          v-else-if="visiblePageActions.length === 1"
          :i="cleanIconClass(visiblePageActions[0].icon)"
          :responsive="false"
          variant="success m-2"
          @click="visiblePageActions[0].click()"
        >
          {{ visiblePageActions[0].name }}
        </action-button>
      </template>
    </page-header>

    <section-title icon="fa fa-edit" content="Tipo de Publicación" />
    <validation-observer ref="formObserver" novalidate tag="form">
      <div class="p-4 bg-gray">
        <i class="far fa-exclamation-circle mr-2"></i>
        <span>
          Seleccione el Tipo de Publicación para comenzar a documentar.
        </span>
        <div class="row mt-3" style="position:relative;bottom: 5px;">
          <div
            v-for="(item, index) in balloons"
            ref="balloons"
            :key="index"
            class="col-sm p-0"
            :class="{
              'cursor-not-allowed': postId,
              'cursor-pointer': !postId,
              'cursor-auto': item.id === model.type,
            }"
            @click="onSelectType(item)"
          >
            <custom-balloon
              class="px-4 w-100 p-3 text-body custom-hover text-black"
              :text="item.name"
              :hex-color="item.hoverColor"
              :hex-text-color="item.textColor"
              :color="
                `${
                  postId && item.id !== model.type
                    ? 'text-muted'
                    : item.textColor
                }`
              "
            />
          </div>
        </div>
      </div>

      <general-info
        v-if="model.type"
        class="mt-3"
        ref="generalInfoRef"
        v-model="model.generalInfo"
        :type="model.type"
        :readOnly="isReadonly"
        @date-changed="resetWorkshopEnrollmentDates"
      />

      <graphic
        v-if="isGraphic"
        v-model="model.graphicInfo"
        :type="model.type"
        :readOnly="isReadonly"
      />

      <news-info
        v-if="(isNews || isEvent || isWorkshop) && isPublicPortal"
        v-model="model.newsInfo"
        :type="model.type"
        :readOnly="isReadonly"
      />

      <documents-section
        v-if="isNews || isWorkshop || isMessage || (isEvent && isPublicPortal)"
        v-model="model.documentsSection"
        :readOnly="isReadonly"
      />

      <tag-selection
        v-if="isNews && isPublicPortal"
        v-model="model.newsInfo.tags"
        :readOnly="isReadonly"
      />

      <categories
        v-if="isEvent && !isPublicPortal"
        v-model="model.generalInfo.calendarCategories"
        :readOnly="isReadonly"
      />

      <div
        class="my-3"
        v-if="(isEvent || isWorkshop || isGraphic) && !isPublicPortal"
      >
        <icon-view
          class="mr-2 mt-3 mb-4 rounded-circle icon-view-color-text"
          icon="fas fa-user text-orange"
          :title="publicationName"
          icon-background="white"
          icon-color="#FF7058"
          icon-size="16px"
          :gradient="true"
        />
        <ValidationProvider
          v-slot="{ errors }"
          name="roles"
          :rules="rolesValidationRule"
        >
          <div class="row">
            <div class="col-12">
              <user-role-selection
                :role-list="roles"
                :selected-roles="model.generalInfo?.accessRoles"
                :show-user-selection="false"
                :readOnly="isReadonly"
                @on-select-rol="onSelect"
                @on-unselect-role="onUnSelectRole"
                @on-unselect-all="onUnSelectAll"
              />
            </div>
            <div v-if="errors.length" class="w-100 d-inline custom-error-msg">
              <i class="fa fa-times-circle mr-2"></i>
              <span>Debes seleccionar al menos un rol</span>
            </div>
          </div>
        </ValidationProvider>
      </div>

      <location-info
        v-if="(isEvent || isWorkshop) && isPublicPortal"
        :regions.sync="collections.locationInfo.regions"
        :readOnly="isReadonly"
        v-model="model.locationInfo.regions"
      />

      <contact-info
        v-if="(isEvent || isWorkshop) && isPublicPortal"
        v-model="model.contactInfo"
        :readOnly="isReadonly"
      />
      <workshop-info
        ref="workshopInfoRef"
        v-if="isWorkshop && !isPublicPortal"
        v-model="model.workshopInfo"
        :readOnly="isReadonly"
        :post-id="postId"
        :enrollment-limit="{
          date: model.generalInfo.end.date,
          hour: model.generalInfo.end.hour,
          minute: model.generalInfo.end.minute,
          type: model.generalInfo.end.type,
        }"
        @partial-update="onPartialUpdate"
      />
      <message-info
        v-if="isMessage"
        ref="messageInfoRef"
        v-model="model.messageInfo"
        :readOnly="isReadonly"
      />

      <teaching-material-info
        v-if="isTeachingMaterial"
        ref="teachingMaterialRef"
        v-model="model.teachingMaterialInfo"
        :readOnly="isReadonly"
      />
    </validation-observer>
  </div>
</template>
<script>
import WorkshopInfo from './components/WorkshopInfo.vue';
import ActionButton from 'ActionButton';
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import { CustomBalloonLight as CustomBalloon } from '@/components/common';
import {
  getTypes,
  save,
  get,
  getWorkshopTests,
  changePostStatus,
} from '@/services/operations/dashboard-management';
import GeneralInfo from './components/GeneralInfo.vue';
import DocumentsSection from './components//DocumentsSection.vue';
import LocationInfo from './components/LocationInfo.vue';
import ContactInfo from './components/ContactInfo.vue';
import MessageInfo from './components/MessageInfo.vue';
import NewsInfo from './components/NewsInfo.vue';
import TagSelection from './components/TagSelection.vue';
import storeMixin from '@/mixin/storeGenerator';
import alertMixin from '@/mixin/alert.mixin.js';
import Graphic from './components/Graphic.vue';

import TYPES from '@/utils/constants/operations-dashboard-management/scheduleItemTypes';
import APPS from '@/utils/constants/applications.js';
import objectToFormData from 'object-to-formdata';
import moment from 'moment';
import { hexWithTransparency } from '@/utils/colors.js';
import AUTH_METHODS from '@/utils/constants/operations-dashboard-management/authMethods';
import TeachingMaterialInfo from './components/TeachingMaterialInfo.vue';
import Categories from './components/Categories.vue';
import IconView from 'icon-view';
import UserRoleSelection from '@/components/common/UserRoleSelection';
import { modulesName } from '@/modules';
import {
  getterTypes,
  actionTypes,
} from '@/modules/operation-message/store/types';
import QuestionnaireClassificationType from '@/utils/constants/operations-dashboard-management/QuestionnaireClassificationType';
import store from '@/store/index';
import DropDown from 'dropdown';
const { mapGetters, mapActions } = createNamespacedHelpers(
  modulesName.operationMessageModuleName
);
import { createNamespacedHelpers } from 'vuex';

import { copy, equals } from '@/utils/objUtil';
import { getSysparameterByNames } from '@/modules/system-parameter-managment/api';

const DASHBOARD_MAINTENANCE_MANAGEMENT = 'dashboardMaintenanceManagement';
const DASHBOARD_MAINTENANCE = 'dashboardMaintenance';
const format = 'h:mm A';

export default {
  name: 'DashboardMaintenanceManagement',
  components: {
    PageHeader,
    ActionButton,
    SectionTitle,
    CustomBalloon,
    GeneralInfo,
    DocumentsSection,
    LocationInfo,
    ContactInfo,
    WorkshopInfo,
    MessageInfo,
    TeachingMaterialInfo,
    NewsInfo,
    TagSelection,
    Categories,
    IconView,
    UserRoleSelection,
    Graphic,
    DropDown,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      page: {
        icon: 'fas fa-calculator',
        title: 'Añadir Publicación',
        routes: [
          'Inicio',
          'Operaciones',
          'Mantenimiento de Dashboard',
          'Añadir',
        ],
      },
      balloons: [],
      collections: {
        locationInfo: {
          regions: [],
        },
      },
      model: {},
      initialModel: {},
      comesFromAdd: false,
      isEditing: false,
      dashboardNewsAllowedImageSizeInPx: '660x280',
    };
  },
  async beforeMount() {
    await this.getScheduleItemTypes();
    await this.initModel();
  },
  async mounted() {
    await this.getSysParams();
    this[actionTypes.FETCH_ROL]();
  },
  computed: {
    visiblePageActions() {
      return this.pageActions.filter((a) => a.visible);
    },
    postId() {
      const id = this.$route.params?.id || this.model?.id || 0;
      return Number(id);
    },
    canSubmitWorkshop() {
      return (
        this.isWorkshop && this.postId && !this.model.workshopInfo?.submitted
      );
    },
    isNews() {
      return this.model.type === TYPES.News;
    },
    isEvent() {
      return this.model.type === TYPES.Event;
    },
    isWorkshop() {
      return this.model.type === TYPES.Workshop;
    },
    isGraphic() {
      return this.model.type === TYPES.Graphic;
    },
    isMessage() {
      return this.model.type === TYPES.Message;
    },
    isTeachingMaterial() {
      return this.model.type === TYPES.TeachingMaterial;
    },
    isEducationalMaterial() {
      return this.model.type === TYPES.EducationalMaterial;
    },
    isPublicPortal() {
      const { id } = this.model.generalInfo.portal ?? {};
      return id === APPS.PublicPortal.toLowerCase();
    },
    isParentPortal() {
      const { id } = this.model.generalInfo.portal ?? {};
      return id === APPS.Parent.toLowerCase();
    },
    ...mapGetters({
      roles: getterTypes.GET_ROL,
    }),
    rolesValidationRule() {
      return this.model?.generalInfo?.accessRoles?.length >= 1
        ? ''
        : 'required';
    },
    publicationName() {
      switch (this.model.type) {
        case TYPES.Event:
          return 'Seleccione Rol(es) para ver Evento';
        case TYPES.Workshop:
          return 'Seleccione Rol(es) para ver Taller';
        case TYPES.Graphic:
          return 'Seleccione Rol(es) para ver gráfica';
        default:
          return '';
      }
    },
    canManageDashboard() {
      return this.userHasPermissions(
        'operations.dashboard.maintenance.management'
      );
    },
    canViewParticipantsButton() {
      return (
        this.isWorkshop &&
        Boolean(this.postId) &&
        (this.userHasPermissions(
          'menu.operations.dashboard.maintenance.canviewparticipantsbutton'
        ) ||
          this.isOwner)
      );
    },
    editMode() {
      return Boolean(this.postId);
    },
    canSave() {
      return this.isEditing && !this.isReadonly;
    },
    showEdit() {
      const valid = this.editMode && !this.isEditing;
      if (this.isWorkshop) {
        const shouldHide =
          this.model.isActive &&
          this.model.workshopInfo.submitted &&
          this.model.workshopInfo.hasParticipants;

        return valid && !shouldHide;
      }
      return valid;
    },
    isReadonly() {
      return this.editMode && !this.isEditing;
    },
    isOwner() {
      return store.state.userProfile.sub === this.model?.generalInfo?.createdBy;
    },
    isStartTimeSet() {
      return (
        !!this.model.generalInfo?.start?.hour &&
        !!this.model.generalInfo?.start?.minute &&
        !!this.model.generalInfo?.start?.type
      );
    },
    isEndTimeSet() {
      return (
        !!this.model.generalInfo?.end?.hour &&
        !!this.model.generalInfo?.end?.minute &&
        !!this.model.generalInfo?.end?.type
      );
    },
    dashboardNewImageSizeInPx() {
      const [width, height] =
        this.dashboardNewsAllowedImageSizeInPx?.split('x').map(Number) || [];
      return {
        width: width || 660,
        height: height || 280,
      };
    },
    pageActions() {
      return [
        {
          visible: this.canViewParticipantsButton && !this.isEditing,
          name: 'Ver Participantes',
          icon: 'fas fa-users text-green',
          click: () => this.goToParticipants(),
        },
        {
          name: 'Editar',
          icon: 'fas fa-save text-green',
          visible: this.showEdit,
          click: () => this.activateEditMode(),
        },
        {
          name: 'Inactivar',
          icon: 'fas fa-ban text-muted',
          visible:
            this.model.isActive == true &&
            this.canManageDashboard &&
            !this.isEditing &&
            this.model?.id,
          click: () => this.changeStatus(),
        },
        {
          name: 'Activar',
          icon: 'far fa-check-circle text-green',
          visible:
            this.model.isActive == false &&
            this.canManageDashboard &&
            !this.isEditing &&
            this.model?.id,
          click: () => this.changeStatus(),
        },
        {
          name: 'Someter',
          icon: 'fas fa-save text-green',
          visible: this.canSubmitWorkshop && !this.isEditing,
          click: () => this.submit(),
        },
        {
          name: 'Guardar',
          icon: 'fas fa-save text-green',
          visible: this.canSave,
          click: () => this.save(),
        },
        {
          name: 'Añadir',
          icon: 'fas fa-file-plus text-green',
          visible:
            this.canManageDashboard &&
            Boolean(this.model?.id) &&
            !this.isEditing,
          click: async () => {
            this.$router.replace({
              name: DASHBOARD_MAINTENANCE_MANAGEMENT,
              params: { ...this.$route.params, id: undefined },
            });
            this.model = this.getEmptyModel();
            this.initialModel = this.getEmptyModel();
            await this.getScheduleItemTypes();
          },
        },
      ];
    },
  },
  methods: {
    cleanIconClass(icon) {
      return icon?.replace(/\btext-\w+\b/g, '').trim();
    },
    async changeStatus() {
      const action = this.model.isActive ? 'inactivar' : 'activar';
      const canContinue = await this.ShowCancelModal(
        'Confirmacion',
        `¿Está seguro que desea ${action} esta publicación?`,
        'Si',
        'No'
      );
      if (!canContinue) return;
      if (this.isWorkshop && !this.model?.workshopInfo?.submitted) {
        this.ShowToast(
          'Advertencia.',
          'El taller debe estar sometido antes de ser activado.',
          'warning'
        );
        return;
      }
      try {
        await changePostStatus(this.model.id, !this.model.isActive);
        this.ShowToast(
          'Proceso Exitoso.',
          'La información se ha guardado correctamente.',
          'success'
        );
        this.model = await this.getPost(this.model.id);
      } catch (error) {
        this.ShowToast('Error', 'Error al intentar actualizar.', 'error');
      }
    },
    getEmptyModel() {
      return {
        id: null,
        type: null,
        isActive: null,
        generalInfo: {
          portal: null,
          module: null,
          card: null,
          messageLocation: null,
          educationalMaterialCategory: null,
          start: {
            date: null,
            hour: null,
            minute: null,
            type: null,
          },
          end: {
            date: null,
            hour: null,
            minute: null,
            type: null,
          },
          title: null,
          description: null,
          shortDescription: null,
          url: null,
          calendarCategories: [],
          accessRoles: [],
          methodology: null,
          isStartTimeSet: null,
          isEndTimeSet: null,
          sendMobilePushNotification: null,
        },
        documentsSection: {
          thumbnail: {
            title: 'Presione aquí para anejar imagen miniatura',
            subtitle: 'La imagen debe contener un tamaño de 274x137 píxeles',
            visible: () =>
              this.isPublicPortal &&
              (this.isWorkshop || this.isNews || this.isEvent),
            file: null,
            width: 274,
            height: 137,
            errorMessage:
              'La imagen es requerida y debe tener una resolución de 274x137 píxeles.',
          },
          mainImage: {
            title: () =>
              (this.isNews || this.isEvent) && !this.isPublicPortal
                ? 'Presione aquí para anejar imagen de noticia'
                : 'Presione aquí para anejar imagen principal',
            subtitle: () =>
              (this.isNews || this.isEvent) && !this.isPublicPortal
                ? `La imagen debe contener un tamaño de ${this.dashboardNewsAllowedImageSizeInPx} píxeles.`
                : 'La imagen debe contener un tamaño de 815x257 píxeles',
            visible: () =>
              (this.isPublicPortal && (this.isWorkshop || this.isEvent)) ||
              this.isNews,
            file: null,
            width: () =>
              (this.isNews || this.isEvent) && !this.isPublicPortal
                ? this.dashboardNewImageSizeInPx.width
                : 815,
            height: () =>
              (this.isNews || this.isEvent) && !this.isPublicPortal
                ? this.dashboardNewImageSizeInPx.height
                : 257,
            errorMessage: () =>
              this.isNews && !this.isPublicPortal
                ? `La imagen es requerida y debe tener una resolución de ${this.dashboardNewsAllowedImageSizeInPx} píxeles.`
                : 'La imagen es requerida y debe tener una resolución de 815x257 píxeles.',
          },
          document: {
            title: ' Presione aquí para anejar documento',
            subtitle: 'El documento debe ser un archivo .pdf',
            file: null,
            visible: () =>
              (this.isWorkshop && !this.isPublicPortal) || this.isMessage,
            accept: '.pdf',
          },
        },
        newsInfo: {
          category: null,
          area: null,
          tags: [],
          register: null,
          cost: null,
        },
        locationInfo: {
          regions: [],
        },
        graphicInfo: {
          reportType: null,
          graphicType: null,
        },
        contactInfo: {
          organizer: null,
          phone: null,
          email: null,
          locationText: null,
          locationUrl: null,
        },
        workshopInfo: {
          submitted: false,
          tests: [],
          workshopDuration: null,
          auth: {
            code: null,
            methodId: AUTH_METHODS.Unset,
          },
          creditAmount: null,
          creditSubmissionDeadline: null,
          willGenerateCertificate: false,
          prePostTest: false,
          willQuotaLimit: null,
          quotaLimit: null,
          hasCredits: null,
          start: {
            date: null,
            hour: null,
            minute: null,
            type: null,
          },
          end: {
            date: null,
            hour: null,
            minute: null,
            type: null,
          },
          examClosingDate: {
            date: null,
            hour: null,
            minute: null,
            type: null,
          },
        },
        messageInfo: {
          roles: [],
          users: [],
        },
        teachingMaterialInfo: {
          userGuides: [],
          videos: [],
        },
      };
    },
    hexWithTransparency(color, alpha) {
      return hexWithTransparency(color, alpha);
    },
    setHoverColor(color) {
      this.hoverColor = color;
    },
    resetHoverColor() {
      this.hoverColor = null;
    },
    hasModelChanges() {
      return !equals(this.model, this.initialModel);
    },
    dateTimeToMoment(dateTime) {
      if (!dateTime) return;
      const { date, hour, minute, type } = dateTime;
      const time = moment(`${hour}:${minute} ${type}`, format);
      const value = moment(date);

      value.set({
        hour: time.get('hour'),
        minute: time.get('minute'),
      });

      return value;
    },
    formatDateTimeObject(momentDate, isTimeSet = true) {
      const hasTime = momentDate.format('HH:mm:ss') !== null && isTimeSet;
      return {
        date: new Date(momentDate),
        hour: hasTime ? momentDate.format('hh') : null,
        minute: hasTime ? momentDate.format('mm') : null,
        second: hasTime ? momentDate.format('ss') : null,
        type: hasTime ? momentDate.format('A') : null,
      };
    },
    cleanNonCommonFields() {
      //limpiar campos que no son comunes
      this.model.generalInfo.description = null;
      this.model.generalInfo.shortDescription = null;
      this.model.generalInfo.url = null;
      this.model.generalInfo.messageLocation = null;

      this.model.documentsSection.mainImage.file = null;
      this.model.documentsSection.thumbnail.file = null;
      this.model.documentsSection.document.file = null;

      this.model.newsInfo = copy(this.initialModel.newsInfo);
      this.model.workshopInfo = copy(this.initialModel.workshopInfo);
      this.model.graphicInfo = copy(this.initialModel.graphicInfo);
      this.model.contactInfo = copy(this.initialModel.contactInfo);
      this.model.locationInfo = copy(this.initialModel.locationInfo);
      this.model.messageInfo = copy(this.initialModel.messageInfo);
      this.model.teachingMaterialInfo = copy(
        this.initialModel.teachingMaterialInfo
      );
      this.$refs.formObserver.reset();
      this.initialModel = copy(this.model);
    },
    async initModel() {
      this.model = this.getEmptyModel();
      if (this.postId) this.model = await this.getPost(this.postId);
      this.initialModel = copy(this.model);
    },
    async goBack() {
      if (this.isReadonly) {
        this.$router.go(-1);
        return;
      }
      if (this.hasModelChanges()) {
        const { cancelled, confirmed } = await this.showConfirmSavePending();
        if (cancelled) return;
        if (confirmed) {
          const saved = await this.save();
          if (!saved) return;
        }
      }
      if (this.comesFromAdd) this.$router.push({ name: DASHBOARD_MAINTENANCE });
      else this.$router.go(-1);
    },
    async getScheduleItemTypes() {
      const { data } = await getTypes();
      this.balloons = data.map((x) => {
        this.$set(x, 'hoverColor', '#D9E5DF');
        this.$set(x, 'textColor', 'text-dark');
        return x;
      });
    },
    async onSelectType(item) {
      if (!this.$route.params.id) {
        this.isEditing = true;
      }

      if (this.model.type === item.id) return;
      if (this.model.type && this.postId) return;
      if (this.model.type && this.hasModelChanges()) {
        const { cancelled, confirmed } = await this.showConfirmSavePending();
        if (cancelled) return;
        if (confirmed) {
          await this.save();
          return;
        }
      }
      this.model.type = item.id;
      this.balloons.forEach((x) => {
        this.$set(x, 'hoverColor', '#D9E5DF');
        this.$set(x, 'textColor', 'text-dark');
      });
      this.$set(item, 'hoverColor', this.hexWithTransparency(item.color, 0.1));
      this.$set(item, 'textColor', this.hexWithTransparency(item.color, 1));

      this.cleanNonCommonFields();
    },
    async submit() {
      if (this.isWorkshop) {
        const model = this.model.workshopInfo;
        if (model.prePostTest) {
          const types = [
            QuestionnaireClassificationType.PostTest,
            QuestionnaireClassificationType.PreTest,
          ];
          const valid = model.tests.some((x) =>
            types.includes(x.classificationId)
          );
          if (!valid) {
            this.ShowToast(
              'Alerta',
              'Debe crear el formulario de Pre Prueba/Post Prueba en la pantalla de Creación de Configuración de Pruebas.',
              'error'
            );
            return;
          }
        }
        const isExamType =
          model.willGenerateCertificate &&
          model.auth.methodId == AUTH_METHODS.Exam;
        if (isExamType) {
          const valid = model.tests.some(
            (x) => x.classificationId == QuestionnaireClassificationType.Exam
          );
          if (!valid) {
            this.ShowToast(
              'Alerta',
              'Debe crear un formulario de Examen en la pantalla de Creación de Configuración de Pruebas.',
              'error'
            );
            return;
          }
        }
        const confirmation = await this.ShowCancelModal(
          'Atención',
          '¿Está seguro que desea Someter este Taller?',
          'Si',
          'No'
        );

        if (!confirmation) return;
        this.save(true);
      }
    },
    async save(submitted = false) {
      const validations = [
        this.$refs.formObserver.validate(),
        this.$refs.generalInfoRef.validate(),
      ];
      if (this.isMessage)
        validations.push(this.$refs.messageInfoRef.validate());

      if (this.isWorkshop && !this.isPublicPortal) {
        validations.push(this.$refs.workshopInfoRef.validate());
        if (!this.$refs.workshopInfoRef.hasValidChecks()) {
          this.ShowToast(
            'Alerta',
            'Por favor completa los campos requeridos.',
            'error'
          );
          return;
        }
      }
      if (this.isTeachingMaterial) {
        const { videos, userGuides } = this.model.teachingMaterialInfo;
        const validTeachingMaterials = videos.length || userGuides.length;
        if (!validTeachingMaterials) {
          this.ShowToast(
            'Alerta',
            'Por favor agregar por lo menos una Guía de Usuario o un Video.',
            'error'
          );
          return;
        }
      }
      const invalid = (await Promise.all(validations)).includes(false);
      if (invalid) {
        this.ShowToast(
          'Alerta',
          'Por favor completa los campos requeridos.',
          'error'
        );
        return;
      }

      const model = {
        id: this.model.id,
        typeId: this.model.type,
      };

      model.generalInfo = {
        applicationId: this.model.generalInfo.portal?.id,
        moduleId: this.model.generalInfo.module?.id,
        cardId: this.model.generalInfo.card?.id,
        start: this.dateTimeToMoment(this.model.generalInfo.start)?.toDate(),
        end: this.dateTimeToMoment(this.model.generalInfo.end)?.toDate(),
        title: this.model.generalInfo.title,
        description: this.model.generalInfo.description,
        shortDescription: this.model.generalInfo.shortDescription,
        url: this.model.generalInfo.url,
        messageLocationId: this.model.generalInfo.messageLocation?.id,
        calendarCategories: this.model.generalInfo.calendarCategories,
        accessRoles: this.model.generalInfo.accessRoles,
        methodologyId: this.model.generalInfo.methodology?.id,
        isStartTimeSet: this.isStartTimeSet,
        isEndTimeSet: this.isEndTimeSet,
        sendMobilePushNotification: this.isParentPortal
          ? this.model.generalInfo.sendMobilePushNotification
          : null,
      };

      if (this.isTeachingMaterial) {
        model.generalInfo.start = null;
        model.generalInfo.end = null;
        model.generalInfo.isStartTimeSet = null;
        model.generalInfo.isEndTimeSet = null;
        model.teachingMaterialInfo = {
          videos: this.model.teachingMaterialInfo.videos,
        };
      }
      if (this.isEvent || (this.isWorkshop && this.isPublicPortal)) {
        model.eventInfo = {
          ...this.model.contactInfo,
          ...this.model.locationInfo,
        };
      }
      if (this.isGraphic) {
        model.graphicInfo = {
          reportTypeId: Number(this.model.graphicInfo?.reportType?.id),
          graphicTypeId: Number(this.model.graphicInfo?.graphicType?.id),
        };
      }
      if (this.isWorkshop && !this.isPublicPortal) {
        model.workshopInfo = {
          submitted: this.model.workshopInfo?.submitted
            ? this.model.workshopInfo?.submitted
            : submitted,
          workshopDuration: this.model.workshopInfo?.workshopDuration,
          hasCredits: this.model.workshopInfo?.hasCredits,
          creditAmount: this.model.workshopInfo?.creditAmount,
          creditSubmissionDeadline: this.model.workshopInfo
            ?.creditSubmissionDeadline,
          willGenerateCertificate: this.model.workshopInfo
            ?.willGenerateCertificate,
          prePostTest: this.model.workshopInfo?.prePostTest,
          authMethodId: this.model.workshopInfo?.auth?.methodId,
          authCode: this.model.workshopInfo?.auth?.code,
          willQuotaLimit: this.model.workshopInfo?.willQuotaLimit,
          quotaLimit: this.model.workshopInfo?.quotaLimit,
          start: this.model.workshopInfo?.start?.date
            ? this.dateTimeToMoment(this.model.workshopInfo?.start)?.toDate()
            : null,
          end: this.model.workshopInfo?.end?.date
            ? this.dateTimeToMoment(this.model.workshopInfo?.end)?.toDate()
            : null,
          examClosingDate: this.model.workshopInfo?.examClosingDate?.date
            ? this.dateTimeToMoment(
                this.model.workshopInfo?.examClosingDate
              )?.toDate()
            : null,
        };
      } else if (this.isWorkshop && this.isPublicPortal) {
        model.workshopInfo = {
          submitted: this.model.workshopInfo?.submitted
            ? this.model.workshopInfo?.submitted
            : submitted,
        };
      }
      if (
        (this.isNews || this.isEvent || this.isWorkshop) &&
        this.isPublicPortal
      ) {
        model.newsInfo = {
          categoryId: Number(this.model.newsInfo?.category?.id),
          areaId: Number(this.model.newsInfo?.area?.id),
          tags: this.model.newsInfo?.tags,
          cost: Number(this.model.newsInfo?.cost),
          register: this.model.newsInfo?.register,
        };
      }
      if (this.isMessage) {
        model.messageInfo = {
          roles: this.model.messageInfo?.roles,
          users: this.model.messageInfo?.users,
        };
      }
      if (this.isEducationalMaterial) {
        model.generalInfo.start = null;
        model.generalInfo.end = null;
        model.generalInfo.isStartTimeSet = null;
        model.generalInfo.isEndTimeSet = null;
        model.educationalMaterialInfo = {
          educationalMaterialCategoryId: this.model.generalInfo
            .educationalMaterialCategory?.id,
        };
      }

      const payload = objectToFormData(model, { indices: true });

      if (this.model.documentsSection.document.file instanceof File)
        payload.append('document', this.model.documentsSection.document.file);
      if (this.model.documentsSection.mainImage.file instanceof File)
        payload.append('mainImage', this.model.documentsSection.mainImage.file);
      if (this.model.documentsSection.thumbnail.file instanceof File)
        payload.append(
          'thumbnailImage',
          this.model.documentsSection.thumbnail.file
        );

      if (this.isTeachingMaterial) {
        this.model.teachingMaterialInfo.userGuides.forEach((x, i) => {
          payload.append(`userGuides`, x.file);
          payload.append(`teachingMaterialInfo[guides][${i}][name]`, x.name);
          if (x.id) {
            payload.append(`teachingMaterialInfo[guides][${i}][id]`, x.id);
          }
        });
      }

      try {
        const { data: id } = await save(payload);
        this.ShowToast(
          'Éxito',
          'Se ha guardado la publicación correctamente',
          'success'
        );
        this.isEditing = false;

        let forceCheck = false;

        if (!this.postId) {
          this.$router.push({
            name: DASHBOARD_MAINTENANCE_MANAGEMENT,
            params: { id },
          });
          this.comesFromAdd = true;
          this.isEditing = true;
          forceCheck = true;
        }

        this.model = await this.getPost(id);

        if (forceCheck) {
          this.model.workshopInfo.willGenerateCertificate = null;
          this.model.workshopInfo.prePostTest = null;
        }
        this.initialModel = copy(this.model);
        return true;
      } catch (error) {
        this.ShowToast('Error', error?.response?.data.message, 'error');
      }
    },
    async onPartialUpdate(resolve) {
      const success = await this.save();
      if (success) {
        this.isEditing = true;
        resolve();
      }
    },
    async getPost(id) {
      try {
        const { data } = await get(id);
        const type = this.balloons.find((x) => x.id === data.type);
        this.onSelectType(type);

        const start = moment(data.generalInfo.start);
        data.generalInfo.start = this.formatDateTimeObject(
          start,
          data.generalInfo.isStartTimeSet
        );

        const end = moment(data.generalInfo.end);
        data.generalInfo.end = this.formatDateTimeObject(
          end,
          data.generalInfo.isEndTimeSet
        );

        if (data.type === TYPES.Workshop) {
          data.workshopInfo.tests = [];
          if (data.workshopInfo.start) {
            const workshopStart = moment(data.workshopInfo.start);
            data.workshopInfo.start = this.formatDateTimeObject(workshopStart);
          } else {
            data.workshopInfo.start = {
              date: null,
              hour: null,
              minute: null,
              type: null,
            };
          }
          if (data.workshopInfo.end) {
            const workshopEnd = moment(data.workshopInfo.end);
            data.workshopInfo.end = this.formatDateTimeObject(workshopEnd);
          } else {
            data.workshopInfo.end = {
              date: null,
              hour: null,
              minute: null,
              type: null,
            };
          }

          if (data.workshopInfo.examClosingDate) {
            const examClosingDate = moment(data.workshopInfo.examClosingDate);
            data.workshopInfo.examClosingDate = this.formatDateTimeObject(
              examClosingDate
            );
          } else {
            data.workshopInfo.examClosingDate = {
              date: null,
              hour: null,
              minute: null,
              type: null,
            };
          }

          if (data.workshopInfo.prePostTest) {
            const { data: test } = await getWorkshopTests({
              postId: this.postId,
              classificationId: QuestionnaireClassificationType.PreTest,
            });
            if (test) data.workshopInfo.tests.push(test);
          }
          if (
            data.workshopInfo.willGenerateCertificate &&
            data.workshopInfo.auth.methodId == AUTH_METHODS.Exam
          ) {
            const { data: test } = await getWorkshopTests({
              postId: this.postId,
              classificationId: QuestionnaireClassificationType.Exam,
            });
            if (test) data.workshopInfo.tests.push(test);
          }
        }

        for (const prop in this.model.documentsSection) {
          const doc = data.documentsSection[prop];
          data.documentsSection[prop] = this.model.documentsSection[prop];
          data.documentsSection[prop].file = doc.id ? doc : null;
        }

        return data;
      } catch (error) {
        this.ShowToast('Error', error?.response?.data.message, 'error');
      }
    },
    async showConfirmSavePending() {
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        '<div>Cuenta con cambios sin guardar,</div><div>¿Desea guardar la información previamente documentada?</div>'
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled) return { cancelled: true }; //keep in the screen if don't want to save but stay
      return { confirmed };
    },
    onSelect(isAll, item) {
      if (!isAll && item) {
        this.model.generalInfo.accessRoles.push(item);
      }
      if (isAll) {
        item.forEach((i) => {
          this.model.generalInfo.accessRoles.push(i);
        });
      }
    },
    onUnSelectRole(item) {
      if (item && item.id) {
        this.model.generalInfo.accessRoles = this.model.generalInfo.accessRoles.filter(
          (x) => x.id !== item.id
        );
      }
    },
    onUnSelectAll() {
      this.model.generalInfo.accessRoles = [];
    },
    goToParticipants() {
      this.$router.push({
        name: 'DashboardMaintenanceManagementParticipants',
        params: { id: this.model.id },
        query: { workshopId: this.model.workshopInfo?.id },
      });
    },
    activateEditMode() {
      this.isEditing = true;
    },
    resetWorkshopEnrollmentDates() {
      this.model.workshopInfo.start.date = null;
      this.model.workshopInfo.end.date = null;
      this.model.workshopInfo.examClosingDate.date = null;
    },
    async getSysParams() {
      const paramValue = (
        await getSysparameterByNames({
          parameterNames: ['DashboardNewsAllowedImageSizeInPx'],
        })
      )?.data[0]?.staticValue;
      if (paramValue) {
        this.dashboardNewsAllowedImageSizeInPx = paramValue;
      }
    },
    ...mapActions([actionTypes.FETCH_ROL]),
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const ROUTE_NAME_FROM = 'dashboardMaintenanceConfigureTestForm';
    next(async (vm) => {
      if (from.name == ROUTE_NAME_FROM) {
        vm.recoveryData();
      } else {
        vm.shouldRecovery = false;
      }
    });
  },
};
</script>
<style scoped>
.cursor-not-allowed {
  cursor: not-allowed;
}
.cursor-auto {
  cursor: auto;
}
.custom-error-msg {
  color: #ff5b5c;
  letter-spacing: 0;
  font-size: 14px;
  color: #ff5b5c;
  margin-bottom: 0;
  font-weight: 400;
  margin-left: 1%;
}
</style>
